import { useState, useEffect } from "react"
import { Link } from "react-router-dom"
import ForgotPasswordForm from "../components/ForgotPasswordForm"
import { LoginAd } from "@/modules/auth/login/ui/components/LoginAd"
import { ForgotPasswordAsync } from "@/modules/auth/infrastructure/auth-service"
import { useRecaptcha } from "@/modules/shared/hooks/use-recaptcha"
import { AxiosError } from "axios"
import { HttpStatusCodes } from "@/lib/http-status-codes"

export default function ForgotPasswordPage() {
    const [isLoading, setIsLoading] = useState(false)
    const [isSuccess, setIsSuccess] = useState(false)
    const [errorMessage, setErrorMessage] = useState("")
    const [cooldownUntil, setCooldownUntil] = useState<number | null>(null)
    const { getToken } = useRecaptcha()

    useEffect(() => {
        if (!cooldownUntil) return
        const tick = () => {
            if (Date.now() >= cooldownUntil) {
                setCooldownUntil(null)
            }
        }
        tick()
        const id = setInterval(tick, 1000)
        return () => clearInterval(id)
    }, [cooldownUntil])

    const isCoolingDown = cooldownUntil !== null && Date.now() < cooldownUntil

    const handleSubmit = async (email: string) => {
        setIsLoading(true)
        setErrorMessage("")

        try {
            const captchaToken = await getToken("forgot_password")
            await ForgotPasswordAsync(email, captchaToken)
            setIsSuccess(true)
        } catch (err: unknown) {
            const axiosErr = err as AxiosError
            if (axiosErr?.response?.status === HttpStatusCodes.TOO_MANY_REQUESTS) {
                const retryAfter = parseInt(
                    (axiosErr.response.headers as Record<string, string>)?.['retry-after'] ?? '60',
                    10
                )
                setCooldownUntil(Date.now() + retryAfter * 1000)
                return
            }
            const message = err instanceof Error ? err.message : "Failed to send password reset email. Please try again."
            setErrorMessage(message)
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div className="flex flex-col md:flex-row w-full min-h-screen">
            <LoginAd />
            <div className="flex-1 flex items-center justify-start">
                <div className="w-full max-w-full pr-4 md:pl-30">
                    {isSuccess ? (
                        <div className="w-full md:w-[75%] justify-center items-center">
                            <div className="bg-card rounded-lg p-8 shadow-sm border border-border">
                                <div className="text-center">
                                    <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
                                        <svg
                                            className="h-6 w-6 text-green-600"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                        >
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth="2"
                                                d="M5 13l4 4L19 7"
                                            />
                                        </svg>
                                    </div>
                                    <h2 className="text-2xl font-bold text-foreground mb-2">
                                        Check your email
                                    </h2>
                                    <p className="text-muted-foreground mb-6">
                                        If that email exists on our system, a password reset link has been sent.
                                        Please check your inbox and follow the instructions.
                                    </p>
                                    <Link
                                        to="/login"
                                        className="inline-flex items-center justify-center px-6 py-3 text-base font-medium text-white rounded-md"
                                        style={{ backgroundColor: "#1A237E" }}
                                    >
                                        Back to login
                                    </Link>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <ForgotPasswordForm
                            onSubmit={handleSubmit}
                            isLoading={isLoading}
                            errorMessage={errorMessage}
                            isCoolingDown={isCoolingDown}
                        />
                    )}
                </div>
            </div>
        </div>
    )
}
